import {
  Loader2,
  CheckCircle2,
  XCircle,
  ScanSearch,
  Eye,
  Tag,
  Type,
  Download,
  Film,
  Split,
  Sparkles,
  Scissors,
} from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import JobStatusBadge from '@/components/jobs/JobStatusBadge';
import { cn } from '@/lib/utils';
import type { Job } from '@/types/job';

interface JobProgressProps {
  job: Job;
}

interface PipelineStep {
  id: string;
  label: string;
  icon: React.ReactNode;
  progressRange: [number, number];
}

const PIPELINE_STEPS: PipelineStep[] = [
  { id: 'download', label: 'Downloading video', icon: <Download className="h-4 w-4" />, progressRange: [0, 10] },
  { id: 'chunk', label: 'Splitting into chunks', icon: <Split className="h-4 w-4" />, progressRange: [10, 15] },
  { id: 'analyze', label: 'Analyzing with Gemini', icon: <Sparkles className="h-4 w-4" />, progressRange: [15, 50] },
  { id: 'extract', label: 'Extracting clips', icon: <Scissors className="h-4 w-4" />, progressRange: [50, 95] },
  { id: 'finalize', label: 'Finalizing', icon: <Film className="h-4 w-4" />, progressRange: [95, 100] },
];

function getStepStatus(step: PipelineStep, progress: number, jobStatus: string) {
  if (jobStatus === 'completed') return 'done';
  if (jobStatus === 'failed') {
    if (progress >= step.progressRange[0]) return 'failed';
    return 'pending';
  }
  if (progress >= step.progressRange[1]) return 'done';
  if (progress >= step.progressRange[0]) return 'active';
  return 'pending';
}

function getStepDetail(step: PipelineStep, statusMessage: string, progress: number, segmentCount: number): string {
  const msg = statusMessage.toLowerCase();
  const status = getStepStatus(step, progress, 'analyzing');

  if (status === 'active') {
    if (step.id === 'download') {
      const sizeMatch = statusMessage.match(/Downloaded (\S+)/i);
      if (sizeMatch) return `Downloaded ${sizeMatch[1]}`;
      return 'Fetching from storage...';
    }
    if (step.id === 'chunk') {
      const createdMatch = statusMessage.match(/chunk (\d+)\s*(of|\/)\s*(\d+)/i);
      if (createdMatch) {
        return `Created chunk ${createdMatch[1]} of ${createdMatch[3]}`;
      }
      const splitMatch = statusMessage.match(/into (\d+) chunks/i);
      if (splitMatch) return `Splitting into ${splitMatch[1]} chunks...`;
      return 'Splitting with FFmpeg...';
    }
    if (step.id === 'analyze') {
      const chunkMatch = statusMessage.match(/chunk (\d+)\s*(of|\/)\s*(\d+)/i);
      if (chunkMatch) {
        const detail = `Chunk ${chunkMatch[1]} of ${chunkMatch[3]}`;
        if (segmentCount > 0) return `${detail} — ${segmentCount} scene(s) found`;
        return detail;
      }
      const readyMatch = msg.match(/(\d+) chunks ready/);
      if (readyMatch) return `${readyMatch[1]} chunks ready, starting...`;
      if (segmentCount > 0) return `${segmentCount} scene(s) found so far`;
      return 'Scanning frames...';
    }
    if (step.id === 'extract') {
      const clipMatch = statusMessage.match(/clip (\d+)\s*(of|\/)\s*(\d+)/i);
      if (clipMatch) return `Clip ${clipMatch[1]} of ${clipMatch[3]}`;
      const extractMatch = statusMessage.match(/extracting (\d+)/i);
      if (extractMatch) return `Extracting ${extractMatch[1]} clip(s)...`;
    }
  }

  if (status === 'done') {
    if (step.id === 'download') return 'Complete';
    if (step.id === 'chunk') {
      const totalMatch = statusMessage.match(/(\d+) chunks/i);
      if (totalMatch) return `${totalMatch[1]} chunks`;
    }
    if (step.id === 'analyze' && segmentCount > 0) {
      return `${segmentCount} scene(s) detected`;
    }
  }

  return '';
}

function SearchCriteriaDisplay({ job }: { job: Job }) {
  const criteria = job.searchCriteria;
  if (!criteria) return null;

  if (criteria.naturalQuery) {
    return <p className="text-xs text-muted-foreground italic">&ldquo;{criteria.naturalQuery}&rdquo;</p>;
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {criteria.objectQuery && (
        <Badge variant="outline" className="text-xs gap-1">
          <Eye className="h-3 w-3" />
          {criteria.objectQuery}
        </Badge>
      )}
      {criteria.labelQuery && (
        <Badge variant="outline" className="text-xs gap-1">
          <Tag className="h-3 w-3" />
          {criteria.labelQuery}
        </Badge>
      )}
      {criteria.textQuery && (
        <Badge variant="outline" className="text-xs gap-1">
          <Type className="h-3 w-3" />
          {criteria.textQuery}
        </Badge>
      )}
    </div>
  );
}

function PipelineStepRow({
  step,
  status,
  detail,
  isLast,
}: {
  step: PipelineStep;
  status: string;
  detail: string;
  isLast: boolean;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors',
            status === 'done' && 'bg-primary border-primary text-primary-foreground',
            status === 'active' && 'border-primary text-primary bg-primary/10',
            status === 'pending' && 'border-muted-foreground/30 text-muted-foreground/40',
            status === 'failed' && 'border-destructive text-destructive bg-destructive/10',
          )}
        >
          {status === 'done' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : status === 'active' ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            step.icon
          )}
        </div>
        {!isLast && <div className={cn('w-0.5 h-8 mt-1', status === 'done' ? 'bg-primary' : 'bg-muted-foreground/20')} />}
      </div>
      <div className="pt-1">
        <p
          className={cn(
            'text-sm',
            status === 'active' && 'font-medium text-foreground',
            status === 'done' && 'text-muted-foreground',
            status === 'pending' && 'text-muted-foreground/50',
            status === 'failed' && 'text-destructive',
          )}
        >
          {step.label}
        </p>
        {detail && (
          <p className={cn('text-xs mt-0.5', status === 'active' ? 'text-primary' : 'text-muted-foreground')}>{detail}</p>
        )}
      </div>
    </div>
  );
}

export default function JobProgress({ job }: JobProgressProps) {
  const isProcessing = job.status === 'analyzing' || job.status === 'extracting';
  const segmentCount = job.segments?.length ?? 0;

  return (
    <Card>
      <CardContent className="p-5 space-y-5">
        <div className="flex items-center justify-between">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              {isProcessing ? (
                <ScanSearch className="h-5 w-5 text-primary animate-pulse" />
              ) : job.status === 'completed' ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : job.status === 'failed' ? (
                <XCircle className="h-5 w-5 text-destructive" />
              ) : (
                <Loader2 className="h-5 w-5 animate-spin" />
              )}
              <p className="text-sm font-medium">{job.statusMessage}</p>
            </div>
            <SearchCriteriaDisplay job={job} />
          </div>
          <JobStatusBadge status={job.status} />
        </div>

        {isProcessing && (
          <div className="space-y-1.5">
            <Progress value={job.progress} className="h-2.5" />
            <p className="text-xs text-muted-foreground text-right font-medium">{job.progress}%</p>
          </div>
        )}

        {(isProcessing || job.status === 'completed' || job.status === 'failed') && (
          <div className="space-y-0">
            {PIPELINE_STEPS.map((step, i) => {
              const status = getStepStatus(step, job.progress, job.status);
              const detail = getStepDetail(step, job.statusMessage, job.progress, segmentCount);
              return (
                <PipelineStepRow
                  key={step.id}
                  step={step}
                  status={status}
                  detail={detail}
                  isLast={i === PIPELINE_STEPS.length - 1}
                />
              );
            })}
          </div>
        )}

        {job.status === 'completed' && segmentCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Found {segmentCount} scene(s) with exact in/out points.
            {job.clips?.length > 0 && ` ${job.clips.length} clip(s) extracted.`}
          </p>
        )}

        {job.status === 'completed' && segmentCount === 0 && (
          <p className="text-sm text-muted-foreground">No matching scenes were found in this video.</p>
        )}

        {job.error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-sm text-destructive">{job.error}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
